import { useState, useEffect, useMemo, useRef } from 'react';
import { useFitHeight } from '../hooks/useFitHeight';
import {
  collection, collectionGroup, getDocs, limit,
  doc, onSnapshot, query, orderBy, serverTimestamp, where, writeBatch,
} from 'firebase/firestore';
import { logActivity } from '../utils/activity';
import type { Firestore } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { getClientDb } from '../firebase/clientApp';
import { useAuth } from '../context/AuthContext';
import { canAccessPage } from '../utils/pages';
import AppLayout from '../components/AppLayout';
import { EntityFormModal, TRADE_TYPES, SERVICE_TYPES, isService } from '../components/EntityFormModal';
import { ImportEntitiesModal } from '../components/EntityImportExport';
import { exportEntitiesXlsx } from '../utils/entityExcel';
import { Button, Modal, Badge, EntityTypeBadge, KYCBadge, Dropdown } from '../components/ui';
import { formatAddressLines, formatAddressOneLine, legacyToBillTo } from '../utils/address';
import { getCurrencyName, fmtMoney } from '../utils/currency';
import { useSettings } from '../hooks/useSettings';
import type { Entity, EntityType } from '../types';

// ── Constants ─────────────────────────────────────────────────────────────────


// ── Safe-delete guard ────────────────────────────────────────────────────────
//
// Deleting an Entity doc used to just remove it outright, leaving every
// invoice/memo/advance that still points at its id unable to resolve a name
// — a real risk the user flagged. Before a delete is allowed, this scans
// every collection an entity id can appear in and returns which ones still
// reference it; a non-empty result blocks the delete (see the Delete modal
// below, which offers Disable — isActive: false — as the safe alternative).
// Each check is a single-field `where` + `limit(1)` (cheap, no composite
// index needed) EXCEPT `inventory/{id}/memos`: this repo already avoids
// `where`/`orderBy` on that collectionGroup elsewhere (Dashboard.tsx,
// Memos.tsx) because no composite index exists for it, so this fetches the
// whole group and filters client-side too, same reasoning.
async function findEntityReferences(db: Firestore, entityId: string): Promise<string[]> {
  async function anyMatch(...qs: ReturnType<typeof query>[]) {
    for (const q of qs) {
      const snap = await getDocs(q);
      if (!snap.empty) return true;
    }
    return false;
  }

  const reasons: string[] = [];

  if (await anyMatch(
    query(collection(db, 'sales'), where('toEntityId', '==', entityId), limit(1)),
    query(collection(db, 'sales'), where('brokerEntityId', '==', entityId), limit(1)),
  )) reasons.push('Sale invoices');

  if (await anyMatch(
    query(collection(db, 'invoices'), where('fromEntityId', '==', entityId), limit(1)),
    query(collection(db, 'invoices'), where('brokerEntityId', '==', entityId), limit(1)),
  )) reasons.push('Purchase invoices');

  if (await anyMatch(
    query(collection(db, 'customerAdvances'), where('toEntityId', '==', entityId), limit(1)),
    query(collection(db, 'supplierAdvances'), where('fromEntityId', '==', entityId), limit(1)),
  )) reasons.push('Advance payments');

  let hasMemo = await anyMatch(
    query(collection(db, 'inventory'), where('memoToEntityId', '==', entityId), limit(1)),
  );
  if (!hasMemo) {
    const memoSnap = await getDocs(collectionGroup(db, 'memos'));
    hasMemo = memoSnap.docs.some(d => d.data().entityId === entityId);
  }
  if (hasMemo) reasons.push('Memos (current or past)');

  return reasons;
}

function fmtShortDate(dateStr?: string | null) {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Customize Columns — STEP 2 of the redesign, added on top of Step 1 ──────
// Personal, per-browser preference (localStorage). Actions is excluded — it's
// a controls column, not data.

type ColumnKey =
  | 'company' | 'type' | 'email' | 'accountantEmail' | 'ownerEmail'
  | 'phone' | 'ownerPhone' | 'billTo' | 'shipTo' | 'country' | 'defaultCurrency'
  | 'kyc' | 'vatGst' | 'website' | 'defaultTerms' | 'creditLimit';

interface ColumnDef { key: ColumnKey; label: string; skeletonWidth: number }

// Order here = column order in the table. Every field the Add/Edit Entity
// form collects that isn't already baked permanently into another column
// (Owner Name rides along under Company as a subtitle, see the `company`
// cell below) gets its own toggleable column here, so "Columns" genuinely
// covers everything rather than a curated subset.
// billTo/shipTo widths are capped in the cell itself (see ADDRESS_COL_WIDTH
// below) — a one-line, ellipsis-truncated address, never a column that
// grows with whatever the longest address happens to be.
const ADDRESS_COL_WIDTH = 150;

// What the Filter button says for each type. Several can be ticked at once
// (Customers + Brokers…); none ticked = every trade company.
const FILTER_LABEL: Record<string, string> = {
  customer: 'Customers', supplier: 'Suppliers', broker: 'Brokers',
  shipping: 'Shipping', insurance: 'Insurance', lab: 'Lab',
};
function filterButtonLabel(picked: EntityType[]): string {
  if (picked.length === 0) return 'All';
  if (picked.length <= 2) return picked.map(t => FILTER_LABEL[t]).join(', ');
  return `${picked.length} types`;
}

const COLUMNS: ColumnDef[] = [
  { key: 'company',   label: 'Company',          skeletonWidth: 150 },
  { key: 'type',      label: 'Type',             skeletonWidth: 100 },
  { key: 'email',     label: 'Company Email',    skeletonWidth: 150 },
  { key: 'accountantEmail', label: 'Accountant Email', skeletonWidth: 150 },
  { key: 'ownerEmail', label: 'Owner Email',     skeletonWidth: 150 },
  { key: 'phone',     label: 'Phone',            skeletonWidth: 100 },
  { key: 'ownerPhone', label: 'Owner Phone',     skeletonWidth: 100 },
  { key: 'billTo',    label: 'Bill To',          skeletonWidth: ADDRESS_COL_WIDTH },
  { key: 'shipTo',    label: 'Ship To',          skeletonWidth: ADDRESS_COL_WIDTH },
  { key: 'country',   label: 'Country',          skeletonWidth: 90  },
  { key: 'defaultCurrency', label: 'Currency',   skeletonWidth: 70  },
  { key: 'kyc',       label: 'KYC Status',       skeletonWidth: 90  },
  { key: 'vatGst',    label: 'VAT / GST',        skeletonWidth: 100 },
  { key: 'website',      label: 'Website',       skeletonWidth: 140 },
  { key: 'defaultTerms', label: 'Default Terms', skeletonWidth: 90  },
  { key: 'creditLimit',  label: 'Credit Limit',  skeletonWidth: 110 },
];
const ACTIONS_SKELETON_WIDTH = 40;

const COLUMN_VISIBILITY_KEY = 'entity-visible-columns';
const DEFAULT_VISIBLE: Record<ColumnKey, boolean> =
  Object.fromEntries(COLUMNS.map(c => [c.key, true])) as Record<ColumnKey, boolean>;

// Merges the saved blob over the all-visible default, key-by-key, so a
// column removed from COLUMNS in a future change is silently dropped and a
// column newly added to COLUMNS defaults to visible for existing users —
// never trust the raw parsed object wholesale.
function loadVisibleColumns(): Record<ColumnKey, boolean> {
  try {
    const raw = localStorage.getItem(COLUMN_VISIBILITY_KEY);
    if (!raw) return { ...DEFAULT_VISIBLE };
    const saved = JSON.parse(raw) as Partial<Record<ColumnKey, boolean>>;
    const merged = { ...DEFAULT_VISIBLE };
    for (const c of COLUMNS) if (typeof saved[c.key] === 'boolean') merged[c.key] = saved[c.key]!;
    return merged;
  } catch { return { ...DEFAULT_VISIBLE }; }
}

// ── Icons ─────────────────────────────────────────────────────────────────────

function IconEdit() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <path d="M9.5 2.5l2 2-7 7H2.5v-2l7-7z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  );
}
function IconTrash() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <path d="M2 3.5h10M5.5 3.5V2.5h3v1M4 3.5l.667 8h4.666L10 3.5"
        stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function IconPlus() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <path d="M7 2v10M2 7h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}
function IconBan() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="7" r="5.3" stroke="currentColor" strokeWidth="1.4" />
      <path d="M3.5 3.5l7 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
function IconCircleCheck() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="7" r="5.3" stroke="currentColor" strokeWidth="1.4" />
      <path d="M4.5 7.2l1.7 1.7 3.3-3.8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconFilter() {
  return (
    <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
      <path d="M1.5 2h11L8 7.5v4L6 13V7.5L1.5 2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
  );
}
function IconDots() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <circle cx="7" cy="3" r="1.3" fill="currentColor" />
      <circle cx="7" cy="7" r="1.3" fill="currentColor" />
      <circle cx="7" cy="11" r="1.3" fill="currentColor" />
    </svg>
  );
}
function IconColumns() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <rect x="1.5" y="2" width="3.2" height="10" rx="1" stroke="currentColor" strokeWidth="1.3" />
      <rect x="5.9" y="2" width="3.2" height="10" rx="1" stroke="currentColor" strokeWidth="1.3" />
      <rect x="10.3" y="2" width="2.2" height="10" rx="1" stroke="currentColor" strokeWidth="1.3" />
    </svg>
  );
}
// Same Export / Import glyphs as Purchase.tsx's toolbar (duplicated per-page).
function IconExport() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M7 1v8M4 6.5L7 9.5l3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M1.5 10.5v1.5a1 1 0 001 1h9a1 1 0 001-1v-1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
function IconImport() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M7 9V1M4 5.5L7 8.5l3-3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" transform="rotate(180 7 4.75)" />
    <path d="M1.5 10.5v1.5a1 1 0 001 1h9a1 1 0 001-1v-1.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}

function IconSearch() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="6" cy="6" r="4" stroke="currentColor" strokeWidth="1.4" />
    <path d="M9.5 9.5l2.5 2.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>;
}
// Empty-state icons — muted SVG outlines, never emoji (see CLAUDE.md).
function IconEmptySearch() {
  return <svg width="36" height="36" viewBox="0 0 14 14" fill="none">
    <circle cx="6" cy="6" r="4" stroke="currentColor" strokeWidth="1.1" />
    <path d="M9.5 9.5l2.5 2.5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
  </svg>;
}
function IconEmptyUsers() {
  return <svg width="36" height="36" viewBox="0 0 16 16" fill="none">
    <circle cx="6" cy="5" r="2.7" stroke="currentColor" strokeWidth="1.2" />
    <path d="M1.5 14c0-2.8 2-4.5 4.5-4.5S10.5 11.2 10.5 14" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    <path d="M10.8 3.3a2.5 2.5 0 010 4.8M13 14c0-2.2-1.2-3.8-3-4.4" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
  </svg>;
}
function IconEmptyTruck() {
  return <svg width="36" height="36" viewBox="0 0 16 16" fill="none">
    <rect x="1" y="4" width="8.5" height="7" rx="1" stroke="currentColor" strokeWidth="1.2" />
    <path d="M9.5 6.5h2.5l2 2.3v2.2H9.5" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
    <circle cx="4.2" cy="12.2" r="1.4" stroke="currentColor" strokeWidth="1.1" />
    <circle cx="11.3" cy="12.2" r="1.4" stroke="currentColor" strokeWidth="1.1" />
  </svg>;
}
// ── Detail-row icons (redesigned Entity Detail modal — see EntityDetailModal /
//    DetailRow below) — small, muted, currentColor outlines matching the
//    style of every other icon in this file. ──────────────────────────────
function IconPersonSmall() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="4.3" r="2.3" stroke="currentColor" strokeWidth="1.3" />
    <path d="M2 12c0-2.4 2.24-4 5-4s5 1.6 5 4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>;
}
function IconPhoneSmall() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M3 2h1.8l.9 2.6-1.2 1.1a7 7 0 003.8 3.8l1.1-1.2L12 9.2V11c0 .55-.45 1-1 1C6.6 12 2 7.4 2 3c0-.55.45-1 1-1z"
      stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round" />
  </svg>;
}
function IconMailSmall() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <rect x="1.5" y="3" width="11" height="8" rx="1.2" stroke="currentColor" strokeWidth="1.3" />
    <path d="M2 3.8l5 3.6 5-3.6" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
  </svg>;
}
function IconLinkSmall() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M5.8 8.2l2.4-2.4M5.6 3.8l.6-.6a2.3 2.3 0 013.3 3.3l-.9.9M8.4 10.2l-.6.6a2.3 2.3 0 01-3.3-3.3l.9-.9"
      stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconBadgeCheck() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M7 1.6l1.4 1 1.7-.2.6 1.6 1.5.9-.5 1.6.5 1.6-1.5.9-.6 1.6-1.7-.2L7 12.4l-1.4-1-1.7.2-.6-1.6-1.5-.9.5-1.6-.5-1.6 1.5-.9.6-1.6 1.7.2L7 1.6z"
      stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round" />
    <path d="M4.9 7l1.4 1.4L9.1 5.6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
function IconHashSmall() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M5 1.5L3.5 12.5M10.5 1.5L9 12.5M1.5 5h11M1.5 9h11" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>;
}
function IconPin() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M7 12.5S11 8.6 11 5.5a4 4 0 10-8 0C3 8.6 7 12.5 7 12.5z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
    <circle cx="7" cy="5.5" r="1.4" stroke="currentColor" strokeWidth="1.2" />
  </svg>;
}
function IconCalendarSmall() {
  return <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <rect x="1.5" y="2.8" width="11" height="9.7" rx="1.2" stroke="currentColor" strokeWidth="1.3" />
    <path d="M1.5 5.6h11M4.3 1.3v2.4M9.7 1.3v2.4" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>;
}
function IconCloseSmall() {
  return <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <path d="M2.5 2.5l9 9M11.5 2.5l-9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>;
}

// ── Row actions — 3-dot Edit / Delete menu ────────────────────────────────────

function RowActionsMenu({
  isActive, onEdit, onToggleActive, onDelete, showEdit = true,
}: {
  isActive: boolean; onEdit: () => void; onToggleActive: () => void; onDelete: () => void;
  /** The detail popup shows Edit as its own button, so it leaves it out here. */
  showEdit?: boolean;
}) {
  return (
    <Dropdown
      align="right"
      trigger={() => (
        <button type="button" className="icon-btn-round" title="Actions">
          <IconDots />
        </button>
      )}
    >
      {showEdit && (
        <button type="button" className="dropdown-item" onClick={onEdit}>
          <IconEdit /> Edit
        </button>
      )}
      <button type="button" className="dropdown-item" onClick={onToggleActive}>
        {isActive ? <IconBan /> : <IconCircleCheck />} {isActive ? 'Disable' : 'Enable'}
      </button>
      <button type="button" className="dropdown-item danger" onClick={onDelete}>
        <IconTrash /> Delete
      </button>
    </Dropdown>
  );
}

// ── Detail modal header facts — matches AP / AR / Sales / Memos ──────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-2)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
      {children}
    </div>
  );
}
function TagChip({ children }: { children: React.ReactNode }) {
  return (
    <span style={{
      fontSize: 11.5, fontWeight: 600, color: 'var(--text-2)',
      background: 'var(--surface-2)', border: '1px solid var(--border)',
      borderRadius: 999, padding: '3px 10px',
    }}>
      {children}
    </span>
  );
}

// ── Redesigned detail-modal building blocks — a scannable icon/label/value
//    row list (each in its own muted icon tile, value right-aligned) plus a
//    gradient profile banner up top, replacing FactGrid's boxed-grid look
//    for this modal specifically (a deliberate one-off restyle the user
//    asked for here — every other record-detail modal in the app keeps the
//    FactGrid/boxed-grid convention documented in CLAUDE.md). ─────────────
function DetailRow({ icon, label, value }: { icon: React.ReactNode; label: string; value: React.ReactNode }) {
  return (
    <div className="entity-detail-row">
      <div className="entity-detail-row-icon">{icon}</div>
      <div className="entity-detail-row-label">{label}</div>
      <div className="entity-detail-row-value">{value}</div>
    </div>
  );
}
function DetailSection({ title, rows }: { title: string; rows: { icon: React.ReactNode; label: string; value: React.ReactNode }[] }) {
  if (rows.length === 0) return null;
  return (
    <>
      <SectionLabel>{title}</SectionLabel>
      <div style={{ marginBottom: 20 }}>
        {rows.map(r => <DetailRow key={r.label} {...r} />)}
      </div>
    </>
  );
}

// Gradient banner + avatar-initial + name + type badges + every action the
// modal needs (quick-dial, KYC status, kebab, close) — the profile-card
// header the user asked for, and now the ONLY header: Modal is rendered
// with hideHead, so there's no second, redundant title bar above this one
// with its own duplicate name/KYC-badge/kebab/× — everything lives here,
// once. Lives inside the modal BODY, bleeding to its edges via negative
// margins: CLAUDE.md's Modal-conventions section documents why
// title/subtitle must stay plain single-line content (two earlier,
// unrelated attempts at multi-line/flex header content broke .modal-head's
// alignment) — a full banner+avatar block is exactly the kind of layout
// content that rule is about, which is exactly why it's in the body and
// why .modal-head is skipped entirely here rather than fought with.
function ProfileBanner({ entity, onEdit, onToggleActive, onDelete, onClose }: {
  entity: Entity;
  onEdit: () => void;
  onToggleActive: () => void;
  onDelete: () => void;
  onClose: () => void;
}) {
  const svc = isService(entity.types);
  const active = entity.isActive !== false;
  const initial = (entity.companyName || '?').trim().charAt(0).toUpperCase();
  const callNumber = entity.ownerPhone || entity.phone;
  const emailAddr  = entity.ownerEmail || entity.email;
  return (
    // Sticky within .modal-body's own scroll container — only Status and
    // everything below it scrolls; the banner (name/avatar/actions) stays
    // put, same as a sticky table header. top: -20 (not 0) per explicit
    // request. Opaque background on the info row below is load-bearing, not
    // decorative — without it, Status/Contact Info scroll up and show
    // through behind the pinned banner instead of disappearing underneath.
    <div style={{ margin: '-20px -24px 20px', position: 'sticky', top: -20, zIndex: 2 }}>
      {/* Both gradient stops are mixed against an opaque color (var(--surface)
          / var(--surface-2)), not "transparent" — this band sits inside the
          sticky banner (see below), so once Status/Contact Info scroll up
          behind it, a "transparent" stop would let them show through it. */}
      <div style={{
        height: 84,
        background: 'linear-gradient(135deg, color-mix(in srgb, var(--primary) 60%, var(--surface)), color-mix(in srgb, var(--primary) 18%, var(--surface-2)))',
      }} />
      <div style={{
        padding: '0 24px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12,
        background: 'var(--surface)', borderBottom: '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, minWidth: 0 }}>
          <div style={{
            width: 72, height: 72, borderRadius: '50%', flexShrink: 0, marginTop: -36,
            // Not var(--primary-light) here — it's mixed against transparent
            // in the dark theme (see :root[data-theme="dark"] in index.css),
            // fine for a badge sitting on one flat background but not for
            // this avatar, which straddles the gradient banner above and
            // var(--surface) below: the see-through tint let both show
            // through unevenly, reading as a muddy/patchy circle in dark
            // mode. Mixed against var(--surface) instead — always opaque,
            // in both themes.
            background: 'color-mix(in srgb, var(--primary) 20%, var(--surface))', color: 'var(--primary)',
            border: '4px solid var(--surface)', boxShadow: '0 2px 8px rgba(15,23,42,0.14)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 26, fontWeight: 800,
          }}>
            {initial}
          </div>
          <div style={{ paddingBottom: 4, minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-1)', overflowWrap: 'break-word', wordBreak: 'break-word' }}>
              {entity.companyName || 'Untitled Entity'}
            </div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 5 }}>
              {entity.types.length > 0
                ? entity.types.map(t => <EntityTypeBadge key={t} type={t} />)
                : <span style={{ fontSize: 12, color: 'var(--text-3)' }}>—</span>}
            </div>
          </div>
        </div>
        {/* KYC + Phone as one pair, Email + kebab + Close as the other —
            per the user's exact request ("KYC in front of phone", "close and
            the menu button next to email"). */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 4, flexShrink: 0 }}>
          {!svc && <KYCBadge status={entity.kycStatus} types={entity.types} />}
          {callNumber && (
            <a href={`tel:${callNumber}`} className="modal-icon-btn" title={`Call ${callNumber}`}>
              <IconPhoneSmall />
            </a>
          )}
          {emailAddr && (
            <a href={`mailto:${emailAddr}`} className="modal-icon-btn" title={`Email ${emailAddr}`}>
              <IconMailSmall />
            </a>
          )}
          {/* Edit is the main action here, so it's a visible button; the
              rarer Disable / Delete stay in the ⋮ menu. */}
          <button type="button" onClick={onEdit} className="modal-icon-btn" title="Edit" aria-label="Edit">
            <IconEdit />
          </button>
          <RowActionsMenu
            isActive={active}
            onEdit={onEdit}
            onToggleActive={onToggleActive}
            onDelete={onDelete}
            showEdit={false}
          />
          <button type="button" onClick={onClose} className="modal-icon-btn" title="Close">
            <IconCloseSmall />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Detail modal — opens on row click ─────────────────────────────────────────

function EntityDetailModal({
  entity, onClose, onEdit, onToggleActive, onDelete,
}: {
  entity: Entity | null;
  onClose: () => void;
  onEdit: (e: Entity) => void;
  onToggleActive: (e: Entity) => void;
  onDelete: (e: Entity) => void;
}) {
  const settings = useSettings();
  const { user } = useAuth();
  if (!entity) return null;
  const svc = isService(entity.types);
  const isCustomer = entity.types.includes('customer');
  const active = entity.isActive !== false;
  const accountLabel = entity.types.includes('insurance') ? 'Policy / Account No'
    : entity.types.includes('lab') ? 'Lab Account No'
    : 'Account No';

  // Everything that isn't Contact Info (Sales Defaults, KYC, Service
  // Details, VAT, Account No) — built up conditionally so nothing renders
  // an empty section when an entity has none of a given kind of data.
  const extraSections = (
    <>
      {isCustomer && (
        <DetailSection title="Sales Defaults" rows={[
          { icon: <IconCalendarSmall />, label: 'Default Terms', value: entity.defaultTerms ? `${entity.defaultTerms} days` : '—' },
          { icon: <IconHashSmall />,  label: 'Credit Limit',  value: entity.creditLimitUSD ? fmtMoney(entity.creditLimitUSD, settings.currency) : '—' },
          { icon: <IconHashSmall />,  label: 'Default Keep(%)', value: entity.defaultLessPercent ? `${entity.defaultLessPercent}%` : '—' },
        ]} />
      )}
      {!svc && (entity.kycDocType || entity.kycDocNumber || entity.kycExpiryDate || entity.kycNotes) && (
        <>
          <DetailSection title="KYC Verification" rows={[
            { icon: <IconBadgeCheck />,   label: 'Document Type',   value: entity.kycDocType || '—' },
            { icon: <IconHashSmall />,    label: 'Document Number', value: entity.kycDocNumber || '—' },
            { icon: <IconCalendarSmall />, label: 'Expiry Date',    value: fmtShortDate(entity.kycExpiryDate) },
          ]} />
          {entity.kycNotes && (
            <div style={{ fontSize: 13, color: 'var(--text-2)', whiteSpace: 'pre-wrap', marginTop: -12, marginBottom: 20 }}>
              {entity.kycNotes}
            </div>
          )}
        </>
      )}
      {svc && entity.agentName && (
        <DetailSection title="Service Details" rows={[
          { icon: <IconPersonSmall />, label: 'Agent Name', value: entity.agentName },
        ]} />
      )}
      {entity.vatGstBtw && (
        <DetailSection title="VAT / GST / BTW" rows={[
          { icon: <IconHashSmall />, label: 'VAT / GST / BTW', value: <span style={{ fontFamily: 'var(--font-num)' }}>{entity.vatGstBtw}</span> },
        ]} />
      )}
      {svc && entity.accountNumber && (
        <DetailSection title={accountLabel} rows={[
          { icon: <IconHashSmall />, label: accountLabel, value: <span style={{ fontFamily: 'var(--font-num)' }}>{entity.accountNumber}</span> },
        ]} />
      )}
    </>
  );

  return (
    <Modal
      open
      onClose={onClose}
      hideHead
      size="lg"
      narrow
    >
      {/* Everything the old .modal-head used to carry (name, KYC badge,
          kebab, ×) now lives in ProfileBanner itself — see hideHead above
          and the comment on ProfileBanner. */}
      <ProfileBanner
        entity={entity}
        onEdit={() => onEdit(entity)}
        onToggleActive={() => onToggleActive(entity)}
        onDelete={() => onDelete(entity)}
        onClose={onClose}
      />

      {/* Everything done with this company lives on the Entity History page. */}
      {canAccessPage(user, 'entity-history') && (
        <Link to={`/entity/history?id=${entity.id}`} className="entity-history-link">
          <span>
            <strong>Sales, purchases, payments and open memos</strong>
            <span>See everything done with {entity.companyName || 'this company'}, date by date.</span>
          </span>
          <span className="entity-history-link-go">View history ›</span>
        </Link>
      )}

      <DetailSection title="Status" rows={[
        { icon: <IconBadgeCheck />, label: 'Status', value: <Badge variant={active ? 'active' : 'inactive'}>{active ? 'Active' : 'Inactive'}</Badge> },
      ]} />
      <DetailSection title="Contact Info" rows={[
        { icon: <IconPersonSmall />, label: svc ? 'Contact Name' : 'Owner Name',   value: entity.ownerName || '—' },
        { icon: <IconPhoneSmall />,  label: svc ? 'Contact Phone' : 'Owner Phone', value: entity.ownerPhone || '—' },
        { icon: <IconMailSmall />,   label: svc ? 'Contact Email' : 'Owner Email', value: entity.ownerEmail || '—' },
        { icon: <IconMailSmall />,   label: 'Company Email', value: entity.email || '—' },
        { icon: <IconMailSmall />,   label: 'Accountant Email', value: entity.accountantEmail || '—' },
        { icon: <IconPhoneSmall />,  label: 'Company Phone', value: entity.phone || '—' },
        { icon: <IconPin />,         label: 'Country', value: entity.country || '—' },
        ...(!svc ? [{ icon: <IconHashSmall />, label: 'Default Currency', value: (entity.defaultCurrency ? `${entity.defaultCurrency} — ${getCurrencyName(entity.defaultCurrency)}` : '—') as React.ReactNode }] : []),
        ...(isCustomer ? [{
          icon: <IconLinkSmall />, label: 'Website',
          value: (entity.website
            ? <span style={{ border: '1px solid var(--border)', borderRadius: 6, padding: '2px 9px', display: 'inline-block' }}>
                <a href={/^https?:\/\//i.test(entity.website) ? entity.website : `https://${entity.website}`} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)' }}>{entity.website}</a>
              </span>
            : '—') as React.ReactNode,
        }] : []),
      ]} />
      {extraSections}

      {(() => {
        const bill = entity.billTo ?? legacyToBillTo(entity.address);
        const billLines = formatAddressLines(bill);
        const shipLines = entity.shipToSameAsBillTo ? [] : formatAddressLines(entity.shipTo);
        if (billLines.length === 0 && shipLines.length === 0 && !entity.shipToSameAsBillTo) return null;
        return (
          <>
            <SectionLabel>{svc ? 'Address' : 'Billing & Shipping'}</SectionLabel>
            <div style={{
              display: 'grid', gridTemplateColumns: svc ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))', gap: 1,
              border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', overflow: 'hidden', marginBottom: 20,
            }}>
              <div style={{ background: 'var(--surface-2)', padding: '12px 14px' }}>
                {!svc && <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 500, marginBottom: 6 }}>Bill To</div>}
                {billLines.length
                  ? billLines.map((l, i) => <div key={i} style={{ fontSize: 13, color: 'var(--text-1)', lineHeight: 1.55 }}>{l}</div>)
                  : <div style={{ fontSize: 13, color: 'var(--text-3)' }}>—</div>}
              </div>
              {!svc && (
                <div style={{ background: 'var(--surface-2)', padding: '12px 14px' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 500, marginBottom: 6 }}>Ship To</div>
                  {entity.shipToSameAsBillTo
                    ? <div style={{ fontSize: 13, color: 'var(--text-2)' }}>Same as billing address</div>
                    : shipLines.length
                      ? shipLines.map((l, i) => <div key={i} style={{ fontSize: 13, color: 'var(--text-1)', lineHeight: 1.55 }}>{l}</div>)
                      : <div style={{ fontSize: 13, color: 'var(--text-3)' }}>—</div>}
                </div>
              )}
            </div>
          </>
        );
      })()}

      {svc && (!!entity.shippingModes?.length || !!entity.coverageTypes?.length) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
          {!!entity.shippingModes?.length && (
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 500, marginBottom: 4 }}>Shipping Modes</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {entity.shippingModes.map(m => <TagChip key={m}>{m}</TagChip>)}
              </div>
            </div>
          )}
          {!!entity.coverageTypes?.length && (
            <div>
              <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 500, marginBottom: 4 }}>Coverage Types</div>
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {entity.coverageTypes.map(c => <TagChip key={c}>{c}</TagChip>)}
              </div>
            </div>
          )}
        </div>
      )}

      {entity.notes && (
        <>
          <SectionLabel>Notes</SectionLabel>
          <div style={{ fontSize: 13, color: 'var(--text-2)', whiteSpace: 'pre-wrap', marginBottom: 20 }}>{entity.notes}</div>
        </>
      )}
    </Modal>
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function EntityPage() {
  const { user: me, business: myBusiness } = useAuth();
  const settings = useSettings();
  const [entities,  setEntities]  = useState<Entity[]>([]);
  const [loading,   setLoading]   = useState(true);
  const [picked,    setPicked]    = useState<EntityType[]>([]);
  const togglePicked = (t: EntityType) => setPicked(p => p.includes(t) ? p.filter(x => x !== t) : [...p, t]);
  const [search,    setSearch]    = useState('');
  const [page,      setPage]      = useState(1);
  // Click a header to sort by it, click again to flip (A to Z first).
  const [sortBy,  setSortBy]  = useState<ColumnKey>('company');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [modalMode, setModalMode] = useState<'add' | 'edit' | 'delete' | null>(null);
  const [selected,  setSelected]  = useState<Entity | null>(null);
  const [saving,    setSaving]    = useState(false);
  const [formError, setFormError] = useState('');
  const [detailEntity, setDetailEntity] = useState<Entity | null>(null);
  // Excel import / export (see components/EntityImportExport.tsx).
  const [importOpen, setImportOpen] = useState(false);
  const [importKey,  setImportKey]  = useState(0);     // remounts the modal fresh each open
  const [ioNotice,   setIoNotice]   = useState('');
  const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>(loadVisibleColumns);
  // 'checking' while the live reference scan runs, 'error' if it failed
  // (never treated as "safe" — fails closed), else the list of collections
  // still referencing this entity (empty = safe to delete).
  const [deleteCheck, setDeleteCheck] = useState<'checking' | 'error' | string[]>('checking');

  const PAGE_SIZE = 50;

  useEffect(() => {
    localStorage.setItem(COLUMN_VISIBILITY_KEY, JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  function toggleColumn(key: ColumnKey) {
    setVisibleColumns(v => ({ ...v, [key]: !v[key] }));
  }

  // ── Live subscription ─────────────────────────────────────────────────────

  useEffect(() => {
    const db = getClientDb();
    if (!db) return;
    const unsub = onSnapshot(
      query(collection(db, 'entities'), orderBy('createdAt', 'desc')),
      snap => {
        setEntities(snap.docs.map(d => {
          const data = d.data() as Partial<Entity>;
          // Several fields (types, companyName, ownerName, phone) are
          // required by the Entity type, but a doc written before that
          // field existed — or edited by hand — can still be missing it.
          // Every filter/sort/search/render below assumes these are real
          // values, so normalize once here rather than guarding every call
          // site: a single malformed doc used to crash the whole page (e.g.
          // clicking the Company header to sort, or just typing in search)
          // since nothing downstream expected `undefined`.
          return {
            id: d.id,
            ...data,
            types:       Array.isArray(data.types) ? data.types : [],
            companyName: data.companyName ?? '',
            ownerName:   data.ownerName ?? '',
            phone:       data.phone ?? '',
          } as Entity;
        }));
        setLoading(false);
      },
      () => setLoading(false),
    );
    return unsub;
  }, []);

  // ── Counts + filtered list ────────────────────────────────────────────────

  const counts = useMemo(() => ({
    all:       entities.filter(e => e.types.some(t => TRADE_TYPES.includes(t))).length,
    customer:  entities.filter(e => e.types.includes('customer')).length,
    supplier:  entities.filter(e => e.types.includes('supplier')).length,
    broker:    entities.filter(e => e.types.includes('broker')).length,
    services:  entities.filter(e => e.types.some(t => SERVICE_TYPES.includes(t))).length,
    shipping:  entities.filter(e => e.types.includes('shipping')).length,
    insurance: entities.filter(e => e.types.includes('insurance')).length,
    lab:       entities.filter(e => e.types.includes('lab')).length,
  }), [entities]);

  const visible = useMemo(() => {
    let list = entities;
    if (picked.length === 0) list = list.filter(e => e.types.some(t => TRADE_TYPES.includes(t)));
    else                     list = list.filter(e => e.types.some(t => picked.includes(t)));
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(e =>
        (e.companyName ?? '').toLowerCase().includes(q) ||
        (e.ownerName ?? '').toLowerCase().includes(q)   ||
        (e.email ?? '').toLowerCase().includes(q) ||
        (e.phone ?? '').includes(q) ||
        (e.country ?? '').toLowerCase().includes(q) ||
        (e.billTo?.city ?? '').toLowerCase().includes(q) ||
        (e.billTo?.country ?? '').toLowerCase().includes(q)
      );
    }
    // Sort: empty values always go last, whichever direction.
    const KYC_RANK: Record<string, number> = { verified: 0, pending: 1, 'not-submitted': 2 };
    const keyOf = (e: Entity): string | number | null => {
      switch (sortBy) {
        case 'company':         return e.companyName?.trim().toLowerCase() || null;
        case 'type':            return e.types.join(',') || null;
        case 'email':           return e.email?.toLowerCase() || null;
        case 'accountantEmail': return e.accountantEmail?.toLowerCase() || null;
        case 'ownerEmail':      return e.ownerEmail?.toLowerCase() || null;
        case 'phone':           return e.phone || null;
        case 'ownerPhone':      return e.ownerPhone || null;
        case 'billTo':          return (formatAddressOneLine(e.billTo) || e.address || '').toLowerCase() || null;
        case 'shipTo':          return (e.shipToSameAsBillTo ? (formatAddressOneLine(e.billTo) || e.address) : formatAddressOneLine(e.shipTo) || '')?.toLowerCase() || null;
        case 'country':         return (e.country || e.billTo?.country || '').toLowerCase() || null;
        case 'defaultCurrency': return e.defaultCurrency || null;
        case 'kyc':             return isService(e.types) ? null : (KYC_RANK[e.kycStatus] ?? 3);
        case 'vatGst':          return e.vatGstBtw?.toLowerCase() || null;
        case 'website':         return e.website?.toLowerCase() || null;
        case 'defaultTerms':    return e.defaultTerms ?? null;
        case 'creditLimit':     return e.creditLimitUSD ?? null;
      }
    };
    const sign = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const ka = keyOf(a), kb = keyOf(b);
      if (ka === null && kb === null) return 0;
      if (ka === null) return 1;
      if (kb === null) return -1;
      const c = typeof ka === 'number' && typeof kb === 'number' ? ka - kb : String(ka).localeCompare(String(kb));
      return c * sign;
    });
  }, [entities, picked, search, sortBy, sortDir]);

  function toggleSort(key: ColumnKey) {
    if (sortBy === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(key); setSortDir('asc'); }
    setPage(1);
  }

  const isServiceView = picked.length > 0 && picked.every(t => SERVICE_TYPES.includes(t));

  // Reset page when filter or search changes
  useEffect(() => setPage(1), [picked, search]);

  const totalPages = Math.ceil(visible.length / PAGE_SIZE);
  const paginated  = visible.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  // Same footer as Sales / Purchase / Inventory: the table ends just above
  // the screen's bottom edge with the page numbers under it.
  const entityWrapRef = useRef<HTMLDivElement>(null);
  useFitHeight(entityWrapRef, 56, 280, [paginated.length > 0, loading]);

  // ── Modal helpers ─────────────────────────────────────────────────────────

  function openAdd() {
    setFormError('');
    setSelected(null);
    setModalMode('add');
  }

  function openEdit(entity: Entity) {
    setDetailEntity(null);
    setSelected(entity);
    setFormError('');
    setModalMode('edit');
  }

  function openDelete(entity: Entity) {
    setDetailEntity(null);
    setSelected(entity);
    setFormError('');
    setModalMode('delete');
    setDeleteCheck('checking');
    const db = getClientDb();
    if (db) {
      findEntityReferences(db, entity.id).then(setDeleteCheck).catch(() => setDeleteCheck('error'));
    } else {
      setDeleteCheck('error');
    }
  }

  function closeModal() { setModalMode(null); setSelected(null); setFormError(''); }

  // ── Delete ────────────────────────────────────────────────────────────────

  async function handleDelete() {
    if (!selected) return;
    setSaving(true);
    const db = getClientDb();
    if (!db) { setSaving(false); return; }
    try {
      // Re-check right before deleting — the earlier scan (run when the
      // modal opened) could be stale if a new invoice/memo landed on this
      // entity in the meantime.
      const stillReferenced = await findEntityReferences(db, selected.id);
      if (stillReferenced.length > 0) {
        setDeleteCheck(stillReferenced);
        setFormError('A new record was just linked to this entity — deletion blocked. See above.');
        setSaving(false);
        return;
      }
      const batch = writeBatch(db);
      batch.delete(doc(db, 'entities', selected.id));
      if (myBusiness) logActivity(batch, db, myBusiness.id, me, {
        module: 'entity', action: 'delete', recordId: selected.id, recordNo: selected.companyName,
      });
      await batch.commit();
      closeModal();
    } catch (e: unknown) {
      setFormError(e instanceof Error ? e.message : 'Failed to delete.');
    }
    setSaving(false);
  }

  // ── Disable / Enable ──────────────────────────────────────────────────────
  // The safe alternative to deleting — marks the entity Inactive (shown on
  // this page and in its detail modal) while every existing record that
  // references it keeps resolving normally, and it can be re-enabled
  // anytime. Mirrors Business.isActive's own kill-switch pattern in the
  // Admin Portal. Does NOT yet hide it from Purchase/Sales' own
  // customer/supplier/broker pickers — that's a separate follow-up.
  async function handleToggleActive(entity: Entity) {
    const db = getClientDb();
    if (!db) return;
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, 'entities', entity.id), {
        isActive: entity.isActive === false, updatedAt: serverTimestamp(),
      });
      if (myBusiness) logActivity(batch, db, myBusiness.id, me, {
        module: 'entity', action: 'edit', recordId: entity.id, recordNo: entity.companyName,
        note: entity.isActive === false ? 'Enabled' : 'Disabled',
      });
      await batch.commit();
    } catch { /* best-effort — row simply won't update if this fails */ }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <AppLayout
      title="Entity"
      subtitle="Manage customers, suppliers, brokers, shippers, and insurers"
     toolbar={<>
          {/* Left: find. Search, then the type filter (shows what's picked). */}
          <div className="tb-search">
            <span className="tb-search-icon"><IconSearch /></span>
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search company, email, phone…"
              aria-label="Search entities"
            />
            {search && (
              <button type="button" className="tb-search-clear" onClick={() => setSearch('')} title="Clear search" aria-label="Clear search"><svg width="8" height="8" viewBox="0 0 8 8" aria-hidden="true" style={{ display: 'block' }}><path d="M1.5 1.5l5 5M6.5 1.5l-5 5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" /></svg></button>
            )}
          </div>

          <Dropdown
            closeOnInnerClick={false}
            trigger={() => (
              <button type="button" className={`icon-btn${picked.length > 0 ? ' active' : ''}`} style={{ height: 34 }}>
                <IconFilter /> {filterButtonLabel(picked)}
              </button>
            )}
          >
            <button type="button" className={`dropdown-item${picked.length === 0 ? ' active' : ''}`} onClick={() => setPicked([])}>
              All trade
              <span style={{ marginLeft: 'auto', opacity: 0.6, fontSize: 11 }}>{counts.all}</span>
            </button>
            <div className="dropdown-divider" />
            <div className="dropdown-group-label">Trade</div>
            {(['customer', 'supplier', 'broker'] as const).map(f => (
              <label key={f} className="dropdown-item" style={{ cursor: 'pointer' }}>
                <input type="checkbox" checked={picked.includes(f)} onChange={() => togglePicked(f)} />
                {FILTER_LABEL[f]}
                <span style={{ marginLeft: 'auto', opacity: 0.6, fontSize: 11 }}>{counts[f]}</span>
              </label>
            ))}
            <div className="dropdown-divider" />
            <div className="dropdown-group-label">Services</div>
            {(['shipping', 'insurance', 'lab'] as const).map(f => (
              <label key={f} className="dropdown-item" style={{ cursor: 'pointer' }}>
                <input type="checkbox" checked={picked.includes(f)} onChange={() => togglePicked(f)} />
                {FILTER_LABEL[f]}
                <span style={{ marginLeft: 'auto', opacity: 0.6, fontSize: 11 }}>{counts[f]}</span>
              </label>
            ))}
          </Dropdown>

          {(search || picked.length > 0) && (
            <button type="button" className="tb-clear" onClick={() => { setSearch(''); setPicked([]); }}>
              Clear
            </button>
          )}

      </>}
      headerActions={<>
          {/* Right: tools as one joined group, then the main action last. */}
          <div className="tb-group">
            <Dropdown
              align="right"
              closeOnInnerClick={false}
              trigger={() => (
                <button type="button" className="tb-group-btn" title="Columns" aria-label="Columns"><IconColumns /></button>
              )}
            >
              <div className="dropdown-group-label">Show Columns</div>
              {COLUMNS.map(c => (
                <label key={c.key} className="dropdown-item" style={{ cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={visibleColumns[c.key]}
                    onChange={() => toggleColumn(c.key)}
                    style={{ accentColor: 'var(--primary)' }}
                  />
                  {c.label}
                </label>
              ))}
              <div className="dropdown-divider" />
              <button type="button" className="dropdown-item" onClick={() => setVisibleColumns({ ...DEFAULT_VISIBLE })}>
                Select All
              </button>
            </Dropdown>
            <button type="button" className="tb-group-btn" title="Import from Excel" aria-label="Import from Excel"
              onClick={() => { setIoNotice(''); setImportKey(k => k + 1); setImportOpen(true); }}>
              <IconImport />
            </button>
            {/* Export = the companies in the current filter/search, in the
                same Excel format Import reads back. */}
            <button type="button" className="tb-group-btn"
              title={`Export ${visible.length} ${visible.length === 1 ? 'entity' : 'entities'} to Excel`}
              aria-label="Export to Excel"
              disabled={visible.length === 0}
              onClick={() => { setIoNotice(''); exportEntitiesXlsx(visible).catch(() => setIoNotice('Could not create the Excel file.')); }}>
              <IconExport />
            </button>
          </div>
          <Button variant="primary" size="sm" icon={<IconPlus />} onClick={openAdd} style={{ height: 34, boxSizing: 'border-box' }}>
            Add Entity
          </Button>
      </>}>


      {ioNotice && (
        <div className="msg msg-info" style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ flex: 1 }}>{ioNotice}</span>
          <button type="button" className="line-item-link-btn" onClick={() => setIoNotice('')}>Dismiss</button>
        </div>
      )}

      <ImportEntitiesModal
        key={importKey}
        open={importOpen}
        onClose={() => setImportOpen(false)}
        existing={entities}
        onImported={n => { setImportOpen(false); setIoNotice(`Imported ${n} ${n === 1 ? 'entity' : 'entities'}.`); }}
      />

      {/* Table */}
      {loading ? (() => {
        const skeletonWidths = [
          ...COLUMNS.filter(c => visibleColumns[c.key]).map(c => c.skeletonWidth),
          ACTIONS_SKELETON_WIDTH,
        ];
        return (
          <div className="table-wrap">
            <table>
              <thead><tr>{skeletonWidths.map((w, i) => (
                <th key={i}><div className="skeleton" style={{ height: 11, width: w, borderRadius: 4 }} /></th>
              ))}</tr></thead>
              <tbody>{[...Array(6)].map((_, i) => (
                <tr key={i} style={{ opacity: 1 - i * 0.13 }}>
                  {skeletonWidths.map((w, j) => (
                    <td key={j}><div className="skeleton" style={{ height: 13, width: w, borderRadius: 4 }} /></td>
                  ))}
                </tr>
              ))}</tbody>
            </table>
          </div>
        );
      })() : visible.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <div className="empty-state-icon">
              {search || picked.length > 0 ? <IconEmptySearch /> : isServiceView ? <IconEmptyTruck /> : <IconEmptyUsers />}
            </div>
            <div className="empty-state-title">
              {search || picked.length > 0 ? 'No entities match your filter' : 'No entities yet'}
            </div>
            <div className="empty-state-sub">
              {search || picked.length > 0
                ? 'Try adjusting the search or filter above.'
                : 'Click "Add Entity" to get started.'}
            </div>
          </div>
        </div>
      ) : (
        <div ref={entityWrapRef} className="table-wrap table-fit table-freeze-first">
          <table>
            <thead>
              <tr>
                {COLUMNS.filter(c => visibleColumns[c.key]).map(c => (
                  <SortableTh key={c.key} label={c.label} sortKey={c.key} activeKey={sortBy} dir={sortDir}
                    onSort={toggleSort} width={c.key === 'company' ? 240 : undefined} />
                ))}
                <th className="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginated.map(entity => {
                const svc = isService(entity.types);
                return (
                <tr
                  key={entity.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setDetailEntity(entity)}
                >
                  {visibleColumns.company && (
                    <td style={{ maxWidth: 240 }} title={[entity.companyName, entity.ownerName].filter(Boolean).join(' · ')}>
                      <div className="cell-clip" style={{ fontSize: 12, fontWeight: 600 }}>{entity.companyName}</div>
                      <div className="cell-clip" style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 1 }}>{entity.ownerName}</div>
                    </td>
                  )}
                  {visibleColumns.type && (
                    <td>
                      <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        {entity.types.map(t => <EntityTypeBadge key={t} type={t} />)}
                      </div>
                    </td>
                  )}
                  {visibleColumns.email && <td style={{ color: 'var(--text-2)', fontSize: 12 }}>{entity.email || <span style={{ opacity: 0.4 }}>—</span>}</td>}
                  {visibleColumns.accountantEmail && (
                    <td style={{ color: 'var(--text-2)', fontSize: 12 }}>{entity.accountantEmail || <span style={{ opacity: 0.4 }}>—</span>}</td>
                  )}
                  {visibleColumns.ownerEmail && (
                    <td style={{ color: 'var(--text-2)', fontSize: 12 }}>{entity.ownerEmail || <span style={{ opacity: 0.4 }}>—</span>}</td>
                  )}
                  {visibleColumns.phone && <td style={{ color: 'var(--text-2)', fontSize: 12, whiteSpace: 'nowrap' }}>{entity.phone || <span style={{ opacity: 0.4 }}>—</span>}</td>}
                  {visibleColumns.ownerPhone && (
                    <td style={{ color: 'var(--text-2)', fontSize: 12, whiteSpace: 'nowrap' }}>{entity.ownerPhone || <span style={{ opacity: 0.4 }}>—</span>}</td>
                  )}
                  {visibleColumns.billTo && (() => {
                    const line = formatAddressOneLine(entity.billTo) || entity.address;
                    return (
                      <td style={{ color: 'var(--text-2)', fontSize: 12 }}>
                        <div
                          style={{ width: ADDRESS_COL_WIDTH, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={line || undefined}
                        >
                          {line || <span style={{ opacity: 0.4 }}>—</span>}
                        </div>
                      </td>
                    );
                  })()}
                  {visibleColumns.shipTo && (() => {
                    const line = entity.shipToSameAsBillTo
                      ? (formatAddressOneLine(entity.billTo) || entity.address)
                      : formatAddressOneLine(entity.shipTo);
                    return (
                      <td style={{ color: 'var(--text-2)', fontSize: 12 }}>
                        <div
                          style={{ width: ADDRESS_COL_WIDTH, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          title={line || undefined}
                        >
                          {line || <span style={{ opacity: 0.4 }}>—</span>}
                        </div>
                      </td>
                    );
                  })()}
                  {visibleColumns.country && (
                    <td style={{ color: 'var(--text-2)', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {(entity.country || entity.billTo?.country) || <span style={{ opacity: 0.4 }}>—</span>}
                    </td>
                  )}
                  {visibleColumns.defaultCurrency && (
                    <td style={{ color: 'var(--text-2)', fontSize: 12 }}>
                      {entity.defaultCurrency || <span style={{ opacity: 0.4 }}>—</span>}
                    </td>
                  )}
                  {visibleColumns.kyc && (
                    <td>{svc ? <span style={{ opacity: 0.4 }}>—</span> : <KYCBadge status={entity.kycStatus} types={entity.types} />}</td>
                  )}
                  {visibleColumns.vatGst && (
                    <td style={{ fontFamily: 'var(--font-num)', fontSize: 12, color: 'var(--text-2)', whiteSpace: 'nowrap' }}>
                      {entity.vatGstBtw || <span style={{ opacity: 0.4, fontFamily: 'inherit' }}>—</span>}
                    </td>
                  )}
                  {visibleColumns.website && (
                    <td style={{ color: 'var(--text-2)', fontSize: 12, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {entity.website
                        ? <a href={/^https?:\/\//i.test(entity.website) ? entity.website : `https://${entity.website}`}
                            target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}
                            style={{ color: 'var(--primary)' }} title={entity.website}>
                            {entity.website}
                          </a>
                        : <span style={{ opacity: 0.4 }}>—</span>}
                    </td>
                  )}
                  {visibleColumns.defaultTerms && (
                    <td style={{ color: 'var(--text-2)', fontSize: 12, fontFamily: 'var(--font-num)' }}>
                      {entity.defaultTerms ? `${entity.defaultTerms}d` : <span style={{ opacity: 0.4, fontFamily: 'inherit' }}>—</span>}
                    </td>
                  )}
                  {visibleColumns.creditLimit && (
                    <td style={{ color: 'var(--text-2)', fontSize: 12, fontFamily: 'var(--font-num)' }}>
                      {entity.creditLimitUSD ? fmtMoney(entity.creditLimitUSD, settings.currency) : <span style={{ opacity: 0.4, fontFamily: 'inherit' }}>—</span>}
                    </td>
                  )}
                  <td className="col-actions">
                    <div className="action-row" onClick={e => e.stopPropagation()}>
                      <RowActionsMenu
                        isActive={entity.isActive !== false}
                        onEdit={() => openEdit(entity)}
                        onToggleActive={() => handleToggleActive(entity)}
                        onDelete={() => openDelete(entity)}
                      />
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Pagination ── */}
      {!loading && visible.length > 0 && (
        <div className="pagination">
          <span className="pagination-info">
            Showing {Math.min((page - 1) * PAGE_SIZE + 1, visible.length)}–{Math.min(page * PAGE_SIZE, visible.length)} of {visible.length} entries
          </span>
          <div className="pagination-btns">
            <button className="pg-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
              .reduce<(number | '…')[]>((acc, p, idx, arr) => {
                if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('…');
                acc.push(p);
                return acc;
              }, [])
              .map((p, i) =>
                p === '…'
                  ? <span key={`e${i}`} className="pg-btn" style={{ cursor: 'default', border: 'none' }}>…</span>
                  : <button key={p} className={`pg-btn${page === p ? ' active' : ''}`} onClick={() => setPage(p as number)}>{p}</button>
              )
            }
            <button className="pg-btn" disabled={page === totalPages || totalPages === 0} onClick={() => setPage(p => p + 1)}>›</button>
          </div>
        </div>
      )}

      {/* ── Add / Edit Modal ── */}
      <EntityFormModal
        open={modalMode === 'add' || modalMode === 'edit'}
        onClose={closeModal}
        entity={modalMode === 'edit' ? selected : null}
        onSaved={closeModal}
      />

      {/* ── Delete Confirm — runs the reference scan above before showing a
             real delete option; if the entity is still linked anywhere, the
             destructive path is replaced by "Disable Instead" (own bespoke
             modal, not the generic ConfirmModal, since the body/footer both
             depend on the live scan's result). ── */}
      <Modal
        open={modalMode === 'delete'}
        onClose={closeModal}
        title="Delete Entity"
        size="sm"
        footer={
          deleteCheck === 'checking' ? (
            <Button variant="secondary" onClick={closeModal}>Cancel</Button>
          ) : deleteCheck === 'error' ? (
            <>
              <Button variant="secondary" onClick={closeModal}>Cancel</Button>
              <Button variant="primary" onClick={() => selected && openDelete(selected)}>Retry Check</Button>
            </>
          ) : deleteCheck.length > 0 ? (
            <>
              <Button variant="secondary" onClick={closeModal}>Cancel</Button>
              <Button variant="primary" onClick={() => { if (selected) handleToggleActive(selected); closeModal(); }}>
                Disable Instead
              </Button>
            </>
          ) : (
            <>
              <Button variant="secondary" onClick={closeModal} disabled={saving}>Cancel</Button>
              <Button variant="danger" onClick={handleDelete} loading={saving}>Delete Entity</Button>
            </>
          )
        }
      >
        {deleteCheck === 'checking' && (
          <p style={{ color: 'var(--text-2)', fontSize: 13 }}>Checking for related records…</p>
        )}
        {deleteCheck === 'error' && (
          <div className="msg msg-error" style={{ margin: 0 }}>
            Couldn't verify whether this entity is safe to delete. Please try again.
          </div>
        )}
        {Array.isArray(deleteCheck) && deleteCheck.length > 0 && (
          <>
            <p style={{ color: 'var(--text-2)', fontSize: 13, lineHeight: 1.6, margin: 0 }}>
              <strong>{selected?.companyName}</strong> can't be deleted — it's still referenced by:
            </p>
            <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 13, color: 'var(--text-2)', lineHeight: 1.7 }}>
              {deleteCheck.map(r => <li key={r}>{r}</li>)}
            </ul>
            <p style={{ color: 'var(--text-2)', fontSize: 13, marginTop: 12, lineHeight: 1.6 }}>
              Deleting it would leave those records unable to show its name. Disable it instead —
              it's marked Inactive but every existing record keeps working, and you can re-enable
              it anytime.
            </p>
          </>
        )}
        {Array.isArray(deleteCheck) && deleteCheck.length === 0 && (
          <p style={{ color: 'var(--text-2)', fontSize: 13, lineHeight: 1.6, margin: 0 }}>
            Are you sure you want to delete <strong>{selected?.companyName}</strong>? No invoices,
            memos, or advance payments reference it, so this is safe — but it still cannot be undone.
          </p>
        )}
        {formError && <div className="msg msg-error" style={{ marginTop: 14 }}>{formError}</div>}
      </Modal>

      {/* ── Detail modal — opens on row click, same convention as every other
             list page in this app (AP/AR/Sales/Purchase/Inventory/Memos). ── */}
      <EntityDetailModal
        entity={detailEntity}
        onClose={() => setDetailEntity(null)}
        onEdit={openEdit}
        onToggleActive={handleToggleActive}
        onDelete={openDelete}
      />
    </AppLayout>
  );
}

function IconSortArrow({ dir }: { dir: 'asc' | 'desc' }) {
  return <svg width="9" height="9" viewBox="0 0 10 10" fill="none"
    style={{ transform: dir === 'asc' ? 'rotate(180deg)' : undefined, transition: 'transform 0.12s' }}>
    <path d="M2 3.5L5 7l3-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>;
}
// Same click-to-sort header as Purchase / Sales.
function SortableTh({ label, sortKey, activeKey, dir, onSort, minWidth, width }: {
  label: string; sortKey: ColumnKey; activeKey: ColumnKey; dir: 'asc' | 'desc';
  onSort: (k: ColumnKey) => void; minWidth?: number;
  /** A fixed width (the frozen Company column): never resizes. */
  width?: number;
}) {
  const active = activeKey === sortKey;
  return (
    <th className={`th-sortable${active ? ' active' : ''}`} style={{ minWidth: width ?? minWidth, width, maxWidth: width }} onClick={() => onSort(sortKey)}>
      <span className="th-sort-inner" style={{ justifyContent: 'flex-start' }}>
        {label}
        <span className="th-sort-arrow"><IconSortArrow dir={active ? dir : 'desc'} /></span>
      </span>
    </th>
  );
}
